import { useLimits } from "@/contexts/LimitsContext";
import { AlertTriangle, Lock } from "lucide-react";

/**
 * Inline banner that shows plan limit status for a feature.
 * Props:
 *   feature - feature key (e.g., "max_photos_per_trip")
 *   current - current count for numeric features
 *   className - extra CSS classes
 *
 * Shows nothing if unlimited or well under limit.
 * Shows warning (yellow) at >= 80%.
 * Shows blocked (red) at 100%.
 */
export function PlanLimitBanner({ feature, current, className = "" }) {
  const { check, plan } = useLimits();
  if (!plan) return null;

  const result = check(feature, current);

  // Boolean feature blocked
  if (typeof result.limit === "boolean" && !result.allowed) {
    return (
      <div className={`flex items-center gap-2 text-xs px-3 py-1.5 rounded bg-red-950/50 border border-red-800/50 text-red-300 ${className}`}
        data-testid={`limit-banner-${feature}`}>
        <Lock className="h-3.5 w-3.5 flex-shrink-0" />
        <span>O teu plano (<strong>{plan.plan_name}</strong>) não inclui esta funcionalidade.</span>
      </div>
    );
  }

  // Numeric: unlimited or no limit defined
  if (result.unlimited || result.allowed === undefined) return null;

  // Blocked
  if (!result.allowed) {
    return (
      <div className={`flex items-center gap-2 text-xs px-3 py-1.5 rounded bg-red-950/50 border border-red-800/50 text-red-300 ${className}`}
        data-testid={`limit-banner-${feature}`}>
        <Lock className="h-3.5 w-3.5 flex-shrink-0" />
        <span>Limite atingido: <strong>{result.current}/{result.limit}</strong>. Faz upgrade do plano <strong>{plan.plan_name}</strong> para continuar.</span>
      </div>
    );
  }

  // Warning (>= 80%)
  if (result.warning) {
    return (
      <div className={`flex items-center gap-2 text-xs px-3 py-1.5 rounded bg-amber-950/50 border border-amber-800/50 text-amber-300 ${className}`}
        data-testid={`limit-banner-${feature}`}>
        <AlertTriangle className="h-3.5 w-3.5 flex-shrink-0" />
        <span>Quase no limite: <strong>{result.current}/{result.limit}</strong> ({result.remaining} restante{result.remaining !== 1 ? "s" : ""}).</span>
      </div>
    );
  }

  return null;
}
